import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { sendWelcomeEmail } from '../utils/sendEmail';
import { mergeLocalIntoAccount, resetMergeGuard } from '../lib/viewHistory';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watchlist, setWatchlist]         = useState([]);
  const [notifications, setNotifications] = useState([]);

  const isPro        = profile?.is_pro === true || profile?.subscription_status === 'active' || profile?.subscription_status === 'pro';
  const hasProAccess = isPro;
  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) setProfile(data);
    return data;
  };

  const fetchWatchlist = async (userId) => {
    const { data } = await supabase
      .from('watchlist')
      .select(`
        listing_id,
        listings (
          id, title, price, price_label, city, area, type,
          beds, baths, sqm, description, features, images, is_featured
        )
      `)
      .eq('user_id', userId);

    if (data) {
      const mapped = data
        .filter(row => row.listings)
        .map(row => {
          const l = row.listings;
          return {
            id:          l.id,
            title:       l.title,
            price:       l.price,
            priceType:   l.price_label,
            type:        l.type,
            bedrooms:    l.beds,
            bathrooms:   l.baths,
            size:        l.sqm,
            city:        l.city,
            region:      l.area,
            description: l.description,
            features:    l.features || [],
            image:       l.images?.[0] || null,
            images:      l.images || [],
            badge:       l.is_featured ? 'Featured' : null,
            gradient:    'linear-gradient(135deg, #7C3AED, #4A1B80)',
            agent:       { name: 'CosmoEstates Agent', phone: '+357 25 000 000', agency: 'CosmoEstates' },
          };
        });
      setWatchlist(mapped);
    }
  };

  useEffect(() => {
    // Resolve session first — unblock the UI immediately, then fetch extras in background
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
        fetchWatchlist(session.user.id);
        // Replay localStorage viewing history into the account. Guarded so
        // it runs once per user per tab session (see lib/viewHistory.js).
        mergeLocalIntoAccount(session.user);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
        fetchWatchlist(session.user.id);
        mergeLocalIntoAccount(session.user);

        if (event === 'SIGNED_IN') {
          setNotifications([
            { id: 1, icon: 'bell', title: 'New listing in Limassol',        desc: 'Modern 4-Bed Villa with Private Pool listed at €620,000.',  time: '2 min ago',   unread: true,  propertyId: 1 },
            { id: 2, icon: 'star', title: 'Price drop alert',               desc: 'Penthouse at Limassol Marina reduced — now €3,500/mo.',     time: '1 hour ago',  unread: true,  propertyId: 6 },
            { id: 3, icon: 'pin',  title: 'New coastal project in Larnaca', desc: '3-bed near Mackenzie Beach from €380,000.',                  time: '3 hours ago', unread: true,  propertyId: 5 },
            { id: 4, icon: 'home', title: 'City centre apartment',          desc: 'Luxury 2-bed in Strovolos, Nicosia — €2,200/mo.',           time: 'Yesterday',   unread: false, propertyId: 2 },
          ]);
        }
      } else {
        setUser(null);
        setProfile(null);
        setWatchlist([]);
        setNotifications([]);
        setLoading(false);
        // Allow the next login (possibly a different account) to merge its
        // own history fresh.
        resetMergeGuard();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Register ──
  const register = async ({ firstName, lastName, email, password, phone }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
          first_name: firstName,
          last_name:  lastName,
          phone:      phone || '',
        },
      },
    });
    if (error) return { success: false, error: error.message };

    // Email verification required when no session is returned.
    // Profile creation + welcome email are deferred to AuthCallback after the
    // user clicks the verification link.
    if (data?.user && !data?.session) {
      return { success: true, needsVerification: true };
    }

    // Email confirmation disabled in Supabase → session created immediately.
    if (data?.user && data?.session) {
      await supabase.from('profiles').insert({
        id:         data.user.id,
        first_name: firstName,
        last_name:  lastName,
        phone:      phone || '',
        is_pro:     false,
      });

      try {
        await sendWelcomeEmail({ firstName, email, userId: data.user.id });
      } catch (e) {
        console.error('Welcome email failed:', e);
      }

      setNotifications([{
        id: 1, icon: 'welcome',
        title: 'Welcome to CosmoEstates!',
        desc: `Hi ${firstName}, your account is ready.`,
        time: 'Just now', unread: true, propertyId: null,
      }]);
    }
    return { success: true, needsVerification: false };
  };

  // ── Login ──
  const login = async ({ email, password }) => {
    if (!email || !password) return { success: false, error: 'Please fill in all fields.' };
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('email not confirmed') || msg.includes('not verified')) {
        return { success: false, error: error.message, needsVerification: true };
      }
      return { success: false, error: 'Invalid email or password.' };
    }

    setUser(data.user);
    await fetchProfile(data.user.id);
    await fetchWatchlist(data.user.id);
    return { success: true };
  };

  // ── Logout ──
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setWatchlist([]);
    setNotifications([]);
  };

  // ── Activate Pro ──
  const activatePro = async ({ planName, amount } = {}) => {
    if (!user) return;
    await supabase.from('profiles').update({ is_pro: true }).eq('id', user.id);
    setProfile(p => ({ ...p, is_pro: true }));

    // Pro welcome email is now sent automatically by the DB trigger
    // (notify_pro_welcome on profiles.is_pro UPDATE)
    // See: supabase/migrations/pro_welcome_trigger.sql
  };

  // ── Watchlist ──
  const addToWatchlist = async (listing) => {
    if (!user) return false;
    setWatchlist(prev => {
      if (prev.find(w => w.id === listing.id)) return prev;
      return [...prev, listing];
    });
    await supabase.from('watchlist').insert({ user_id: user.id, listing_id: listing.id });
    setNotifications(prev => [{
      id: Date.now(), icon: 'welcome',
      title: 'Added to Watchlist',
      desc: `"${listing.title}" in ${listing.city} has been saved.`,
      time: 'Just now', unread: true, propertyId: listing.id,
    }, ...prev]);
    return true;
  };

  const removeFromWatchlist = async (id) => {
    setWatchlist(prev => prev.filter(w => w.id !== id));
    if (user) await supabase.from('watchlist').delete().eq('user_id', user.id).eq('listing_id', id);
  };

  const isInWatchlist = (id) => watchlist.some(w => w.id === id);
  const markAllRead   = () => setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  const unreadCount   = notifications.filter(n => n.unread).length;

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const userObj = user && profile ? {
    ...user,
    firstName: profile.first_name,
    lastName:  profile.last_name,
    email:     user.email,
    phone:     profile.phone,
    avatar:    (profile.first_name || user.email || '?').charAt(0).toUpperCase(),
  } : user ? {
    ...user,
    firstName: user.email?.split('@')[0] || '',
    lastName:  '',
    email:     user.email,
    phone:     '',
    avatar:    (user.email || '?').charAt(0).toUpperCase(),
  } : null;

  if (loading) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#FAF9F6', zIndex: 9999,
      }}>
        <div style={{
          width: 36, height: 36,
          border: '3px solid #B8924A',
          borderTopColor: '#0E0E10',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user: userObj,
      profile,
      rawUser: user,
      loading,
      login, logout, register,
      watchlist, addToWatchlist, removeFromWatchlist, isInWatchlist,
      notifications, markAllRead, unreadCount,
      isPro, hasProAccess, activatePro,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}